import { Component, computed, input, output } from '@angular/core';
import { WorkTimerecord, WorkTimerecorddetail, WorkTimerecorddetailsByDateDto } from '@work-frontend-monorepo/work-rest-api';
import { TranslatePipe } from '@ngx-translate/core';
import dayjs from 'dayjs';
import { ButtonComponent, IconComponent } from '@work-frontend-monorepo/work-ui/layout';
import { FormatDatePipe, FormatTimePipe, RoundToNearestFiveCentsPipe, SortByPipe } from '@work-frontend-monorepo/work-ui/pipes';
import { ChartProgressComponent } from '@work-frontend-monorepo/work-ui/visualization';
import { ConfirmActionModalButton, ConfirmActionModalDirective, ConfirmActionModalOptions } from '@work-frontend-monorepo/work-ui/modals';
import { EDITABLE_TIMERECORDTYPE_IDS } from '../../functions/arbeitszeit-timerecordtypes.const';

type TagAktion = 'reapplyPause' | 'resetDay';

@Component({
  selector: 'sfg-app-arbeitszeit-tageskarte',
  templateUrl: './arbeitszeit-tageskarte.component.html',
  styleUrls: ['./arbeitszeit-tageskarte.component.scss'],
  standalone: true,
  imports: [
    ButtonComponent,
    ChartProgressComponent,
    ConfirmActionModalDirective,
    FormatDatePipe,
    TranslatePipe,
    IconComponent,
    FormatTimePipe,
    RoundToNearestFiveCentsPipe,
    SortByPipe,
  ],
})
export class ArbeitszeitTageskarteComponent {
  // Inputs
  groupedByDate = input.required<WorkTimerecorddetailsByDateDto>();

  // Outputs
  createClick = output<WorkTimerecord | undefined>();
  editClick = output<WorkTimerecorddetail>();
  deleteClick = output<WorkTimerecorddetail>();
  reapplyPauseClick = output<WorkTimerecord>();
  resetDayClick = output<WorkTimerecord>();

  protected tagAktionenModalOptions: ConfirmActionModalOptions = {
    title: 'AKTIONEN',
    buttons: [
      { label: 'PAUSEN_NEU_EINTRAGEN', variant: 'secondary', value: 'reapplyPause', role: 'action' },
      { label: 'TAG_ZURUECKSETZEN', variant: 'warning', value: 'resetDay', role: 'action' },
    ],
  };

  // Computed signals
  canCreate = computed(() => {
    const timerecord = this.groupedByDate().timerecord;
    if (!timerecord?.workdate) {
      return false;
    }
    return this.canEditForDate(timerecord.workdate);
  });

  isEditable = computed(() => {
    const timerecorddetails = this.groupedByDate().timerecorddetails;
    return new Map(timerecorddetails.map((detail) => [detail.id, this.canEditTimerecorddetail(detail)]));
  });

  protected onCreateClick() {
    this.createClick.emit(this.groupedByDate().timerecord);
  }

  protected onEditClick(timerecorddetail: WorkTimerecorddetail) {
    this.editClick.emit(timerecorddetail);
  }

  protected onDeleteClick(timerecorddetail: WorkTimerecorddetail) {
    this.deleteClick.emit(timerecorddetail);
  }

  protected onTagAktionConfirmed(button: ConfirmActionModalButton | undefined) {
    const timerecord = this.groupedByDate().timerecord;
    if (!timerecord || !button) {
      return;
    }
    const action = button.value as TagAktion;
    if (action === 'reapplyPause') {
      this.reapplyPauseClick.emit(timerecord);
    } else if (action === 'resetDay') {
      this.resetDayClick.emit(timerecord);
    }
  }

  private canEditTimerecorddetail(timerecorddetail: WorkTimerecorddetail): boolean {
    // Nicht bearbeitbar wenn locked oder verified
    if (timerecorddetail.locked || timerecorddetail.verified) {
      return false;
    }
    // Nicht bearbeitbar wenn Datum vor aktuellem Montag liegt
    if (timerecorddetail.timerecord?.workdate && !this.canEditForDate(timerecorddetail.timerecord.workdate)) {
      return false;
    }
    // Nur Einträge ohne Typ oder mit einem der erlaubten Typen (Arbeit/Kompensation) dürfen bearbeitet werden —
    // andere Typen (z.B. Pause, Ferien) lassen sich im Modal nicht korrekt abbilden
    if (timerecorddetail.timerecordtypeId != null && !EDITABLE_TIMERECORDTYPE_IDS.includes(timerecorddetail.timerecordtypeId)) {
      return false;
    }
    return true;
  }

  private canEditForDate(dateString: string): boolean {
    const date = dayjs(dateString);
    const currentMonday = dayjs().startOf('week');
    return date.isSameOrAfter(currentMonday, 'day');
  }
}
